import { createContext, useContext, useState, useEffect } from 'react';
import en from '../locales/en';
import ar from '../locales/ar';
import de from '../locales/de';
import es from '../locales/es';
import fr from '../locales/fr';
import pt from '../locales/pt';
import ru from '../locales/ru';
import tr from '../locales/tr';
import zh from '../locales/zh.js';

const LocaleContext = createContext();

const translations = { en, ar, de, es, fr, pt, ru, tr, zh };

const rtlLanguages = ['ar'];

const localeFontMap = {
  ar: "'Noto Sans Arabic', 'Tajawal', sans-serif",
  zh: "'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif",
  en: "'Geist Sans', 'Inter', sans-serif",
  de: "'Geist Sans', 'Inter', sans-serif",
  es: "'Geist Sans', 'Inter', sans-serif",
  fr: "'Geist Sans', 'Inter', sans-serif",
  pt: "'Geist Sans', 'Inter', sans-serif",
  ru: "'Geist Sans', 'Inter', sans-serif",
  tr: "'Geist Sans', 'Inter', sans-serif",
};

export const localeDisplayNames = {
  en: 'English',
  ar: 'العربية',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
  ru: 'Русский',
  tr: 'Türkçe',
  zh: '中文',
};

function getValidLocale(candidate) {
  return translations[candidate] ? candidate : 'en';
}

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    const stored = localStorage.getItem('locale');
    return getValidLocale(stored);
  });

  const isRTL = rtlLanguages.includes(locale);

  useEffect(() => {
    // Persist immediately on every change
    localStorage.setItem('locale', locale);

    document.documentElement.dir  = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
    document.documentElement.style.fontFamily = localeFontMap[locale] ?? localeFontMap.en;
  }, [locale, isRTL]);

  const changeLocale = (newLocale) => {
    const valid = getValidLocale(newLocale);
    setLocale(valid);
  };

const t = (key, vars = {}) => {
  const keys = key.split('.');

  let value = translations[locale];
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) break;
  }

  if (value === undefined) {
    let fallback = translations.en;
    for (const k of keys) {
      fallback = fallback?.[k];
      if (fallback === undefined) break;
    }
    value = fallback ?? key;
  }

  // Replace {variable} placeholders
  if (typeof value === 'string' && Object.keys(vars).length > 0) {
    Object.entries(vars).forEach(([k, v]) => {
      value = value.replaceAll(`{${k}}`, v);
    });
  }

  return value;
};

  const value = {
    locale,
    changeLocale,
    t,
    isRTL,
    availableLocales: Object.keys(translations),
    localeDisplayNames,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
}